import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Briefcase, MapPin, Building2, Clock, X, CheckCircle2 } from 'lucide-react';
import { useContent } from '../hooks/useContent';
import { SectionHeading } from './SectionHeading';

const TYPE_ACCENTS = {
  'Full-Time': 'from-emerald-500 to-teal-500',
  'Part-Time': 'from-amber-500 to-orange-500',
  'Contract': 'from-violet-500 to-fuchsia-500',
  'Internship': 'from-sky-500 to-blue-500',
  'Volunteer': 'from-rose-500 to-pink-500',
};

const Careers = () => {
  const { data, loading } = useContent('careers', []);
  const items = (data || []).filter((c) => c.published !== false);
  const [openJob, setOpenJob] = useState(null);

  if (!loading && items.length === 0) return null;

  return (
    <section
      id="careers"
      className="py-20 md:py-24 bg-gradient-to-b from-white via-blue-50/30 to-white relative overflow-hidden"
      data-testid="careers-section"
    >
      <div className="absolute top-16 -left-20 w-72 h-72 bg-blue-200/30 rounded-full blur-3xl"></div>
      <div className="absolute bottom-16 -right-20 w-72 h-72 bg-amber-200/30 rounded-full blur-3xl"></div>

      <div className="relative max-w-7xl mx-auto px-4">
        <SectionHeading
          eyebrow="Join Us"
          title="Open"
          accent="Positions"
          subtitle="Grow your career while making an impact — explore roles at EWRF and become part of a nationwide movement empowering underserved communities."
        />

        <div className="grid gap-4 md:gap-5">
          {items.map((job) => {
            const accent = TYPE_ACCENTS[job.type] || 'from-blue-500 to-indigo-500';
            return (
              <Card
                key={job.id}
                className="group relative overflow-hidden border-2 border-gray-100 hover:border-transparent hover:shadow-2xl transition-all"
                data-testid={`career-card-${job.id}`}
              >
                <div className={`absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b ${accent}`}></div>
                <CardContent className="p-5 md:p-6 pl-6 md:pl-8">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <Badge className={`bg-gradient-to-r ${accent} text-white border-0 text-[10px] font-black uppercase tracking-widest`}>
                          <Briefcase className="w-3 h-3 mr-1.5" /> {job.type || 'Position'}
                        </Badge>
                        {job.department && (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500">
                            <Building2 className="w-3 h-3" /> {job.department}
                          </span>
                        )}
                        {job.location && (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500">
                            <MapPin className="w-3 h-3" /> {job.location}
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg md:text-xl font-black text-[#0A1628] group-hover:text-[#1565C0] transition-colors">
                        {job.title}
                      </h3>
                      {job.deadline && (
                        <p className="mt-1 inline-flex items-center gap-1.5 text-xs text-rose-600 font-semibold">
                          <Clock className="w-3.5 h-3.5" /> Apply by {new Date(job.deadline).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                    <Button
                      onClick={() => setOpenJob(job)}
                      className="bg-gradient-to-r from-[#0D47A1] to-[#1976D2] text-white font-black uppercase tracking-wider text-xs px-6 py-5 rounded-full shadow-lg hover:shadow-blue-500/40 hover:scale-105 transition-transform flex-shrink-0"
                      data-testid={`career-view-${job.id}`}
                    >
                      View Role
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Detail modal */}
      {openJob && (
        <div
          className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setOpenJob(null)}
          data-testid="career-modal-backdrop"
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="career-modal"
          >
            <div className="sticky top-0 bg-gradient-to-r from-[#0D47A1] to-[#1976D2] text-white p-6 rounded-t-2xl flex items-start justify-between gap-4 z-10">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <Badge className="bg-white/15 text-white border border-white/30 text-[10px] font-black uppercase tracking-widest">
                    {openJob.type || 'Position'}
                  </Badge>
                  {openJob.department && <span className="text-xs text-white/80">{openJob.department}</span>}
                </div>
                <h3 className="text-2xl font-black">{openJob.title}</h3>
                {openJob.location && (
                  <p className="mt-1 text-sm text-white/85 inline-flex items-center gap-1">
                    <MapPin className="w-4 h-4" /> {openJob.location}
                  </p>
                )}
              </div>
              <button
                onClick={() => setOpenJob(null)}
                className="p-2 rounded-full hover:bg-white/15 transition"
                aria-label="Close"
                data-testid="career-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {openJob.description && (
                <div
                  className="prose prose-sm max-w-none text-gray-700"
                  dangerouslySetInnerHTML={{ __html: openJob.description }}
                />
              )}

              {Array.isArray(openJob.responsibilities) && openJob.responsibilities.length > 0 && (
                <div>
                  <h4 className="text-xs font-black uppercase tracking-widest text-[#1565C0] mb-3">
                    Responsibilities
                  </h4>
                  <ul className="space-y-2">
                    {openJob.responsibilities.map((r, i) => (
                      <li key={i} className="flex gap-2 items-start text-sm text-gray-700">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> {r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {Array.isArray(openJob.requirements) && openJob.requirements.length > 0 && (
                <div>
                  <h4 className="text-xs font-black uppercase tracking-widest text-[#1565C0] mb-3">
                    Requirements
                  </h4>
                  <ul className="space-y-2">
                    {openJob.requirements.map((r, i) => (
                      <li key={i} className="flex gap-2 items-start text-sm text-gray-700">
                        <CheckCircle2 className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" /> {r}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <a
                href={`mailto:admin@ewrf.org.my?subject=Application: ${encodeURIComponent(openJob.title)}`}
                className="block"
              >
                <Button
                  className="w-full bg-gradient-to-r from-[#0D47A1] to-[#1976D2] text-white font-black uppercase tracking-wider text-sm py-6 rounded-full shadow-xl hover:shadow-blue-500/50"
                  data-testid="career-apply-btn"
                >
                  Apply for this Role
                </Button>
              </a>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Careers;
